import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const matrix = [
  {
    role: '教师',
    icon: 'ri-user-star-line',
    focus: '怎么改',
    outputs: ['改进建议', '成长变化', '优秀片段'],
    color: 'primary' as const,
  },
  {
    role: '学生',
    icon: 'ri-graduation-cap-line',
    focus: '怎么学',
    outputs: ['学习重点', '视频回看', '课后测验'],
    color: 'accent' as const,
  },
  {
    role: '专家',
    icon: 'ri-shield-user-line',
    focus: '怎么评',
    outputs: ['关键片段', '评价依据', '校准工具'],
    color: 'primary' as const,
  },
  {
    role: '管理者',
    icon: 'ri-bar-chart-grouped-line',
    focus: '怎么管',
    outputs: ['质量趋势', '分档比例', '资源配置'],
    color: 'accent' as const,
  },
];

const evidenceBase = ['课堂原话', '时间戳', '视频片段', '课件画面', '指标映射'];

export default function P66GradingUsage({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative h-screen flex flex-col justify-center overflow-hidden">
      {/* 三层背景光效 */}
      <div ref={orb1Ref} className="absolute top-20 right-24 w-[380px] h-[380px] rounded-full bg-primary-200/30 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-[400px] h-[400px] rounded-full bg-accent-200/25 blur-[110px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />
      <div className="absolute top-1/2 left-1/3 w-[300px] h-[300px] rounded-full bg-secondary-200/20 blur-[140px] pointer-events-none animate-orb-float-3" />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-3 md:py-4">
        <div ref={titleRef} className="text-center mb-2 md:mb-3">
          <span className={`inline-flex items-center gap-2 text-lg font-body font-semibold text-gold-600 uppercase tracking-[0.15em] mb-2 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="gold-dot" />四类视图小结
          </span>
          <h2 className={`font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-2 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            同一套课堂证据，形成<span className="text-gold-600">四种使用视图</span>
          </h2>
          <p className={`text-lg text-foreground-950 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            界面可以不同，证据不能分裂；用途可以不同，事实来源必须一致
          </p>
        </div>

        {/* Four Quadrant Matrix */}
        <div className={`max-w-6xl mx-auto mb-3 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            {matrix.map((m, i) => (
              <div
                key={m.role}
                className="p-4 glass-liquid rounded-xl"
                style={{ transitionDelay: `${300 + i * 120}ms` }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-12 h-12 rounded-xl ${m.color === 'accent' ? 'bg-gold-100/80 border border-gold-300/40' : 'bg-primary-100/60 border border-primary-200/40'} flex items-center justify-center`}>
                    <i className={`${m.icon} text-xl ${m.color === 'accent' ? 'text-gold-600' : 'text-primary-600'}`} />
                  </div>
                  <div>
                    <h4 className="font-heading text-xl font-bold text-foreground-950">{m.role}</h4>
                    <span className={`text-base font-body font-semibold ${m.color === 'accent' ? 'text-gold-600' : 'text-primary-600'}`}>{m.focus}</span>
                  </div>
                </div>
                <div className="space-y-1.5">
                  {m.outputs.map((o) => (
                    <div key={o} className="flex items-center gap-2 text-base text-foreground-950 font-body">
                      <i className="ri-checkbox-blank-circle-fill text-[10px] text-gold-400 flex-shrink-0" />
                      {o}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Evidence Base */}
        <div className={`max-w-5xl mx-auto mb-3 transition-all duration-1000 delay-500 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="p-4 glass-liquid rounded-xl">
            <p className="text-base font-body font-bold text-foreground-950 text-center mb-2">统一连接五类底层证据</p>
            <div className="flex flex-wrap justify-center gap-2">
              {evidenceBase.map((e, i) => (
                <span
                  key={e}
                  className="px-3 py-1.5 rounded-full text-base font-body font-semibold bg-secondary-100/60 text-secondary-700 border border-secondary-200/40"
                  style={{ transitionDelay: `${500 + i * 80}ms` }}
                >
                  {e}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className={`max-w-4xl mx-auto text-center transition-all duration-1000 delay-700 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="p-4 glass-liquid rounded-xl">
            <p className="text-lg text-foreground-950 font-body leading-relaxed">
              系统不需要为四类对象重复生成彼此割裂的结论，而是从<strong className="text-gold-700">同一条课堂证据链</strong>中生成不同视图。一个课堂提问，对教师可以是互动改进建议，对学生可以是重点问题，对专家可以是评价证据，对管理者可以进入互动质量统计。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}